import axios from "axios";
import {
  confirmPaymentUrl,
  checkTransactionUrl,
  docUrl,
  getDocSettingsUrl,
  imagesUrl,
  namesUrl,
  payUrl,
  payCardUrl,
  pledgeUrl,
  salesUrl,
  contributorsListUrl,
  contributorsStatementsUrl,
  statementsUrl,
  contributorPledgeUrl,
  multinamesUrl,
  checkSubUrl,
  surveysUrl,
  surveysSubmitUrl,
  getReceiptDetailsUrl,
  groupContactsUrl,
  groupMemberSearchUrl,
  statsUrl,
} from "../constants/urls";
import { timer } from "../constants/variables";
import { formatPayDate, timeDate } from "./functions";

export const handleViewStats = async (data) => {
  try {
    const response = await axios.post(statsUrl, data, {
      headers: { "Content-Type": "application/json" },
    });
    return { success: true, data: response?.data };
  } catch (error) {
    return { success: false, data: null };
  }
};

export const handleViewStatsBeacon = (data) => {
  try {
    const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
    navigator.sendBeacon(statsUrl, blob);
    return true;
  } catch (error) {
    return false;
  }
};

export const getContributorsList = async (code) => {
  try {
    const response = await axios.get(`${contributorsListUrl}${code}`);
    if (response?.data?.message === "Contributor list is private") {
      return { success: true, data: 0 };
    }
    return { success: true, data: response?.data?.data };
  } catch (error) {
    return { success: false, data: [] };
  }
};

export const checkPledge = async (saleId, name) => {
  let data = {
    sale_id: saleId,
    customer_name: name,
  };
  try {
    const response = await axios.post(contributorPledgeUrl, data);
    if (response?.data.data?.pledge > 0) {
      return { success: true, pledge: response?.data.data?.pledge };
    }
    return { success: true, pledge: 0 };
  } catch (error) {
    return { success: false, pledge: 0 };
  }
};

export const checkSub = async (number, sales_code) => {
  let data = {
    customer_phone: number,
    payment_code: sales_code,
  };
  try {
    const response = await axios.post(checkSubUrl, data);
    if (response?.data?.data?.paid) {
      return { success: true, paid: response?.data?.data?.paid };
    }
    return { success: true, paid: false };
  } catch (error) {
    return { success: false, paid: false };
  }
};

export const handleAsync = async (code, urlOption, urlAmount) => {
  try {
    const response = await axios.get(`${salesUrl}/${code}`);
    if (response?.data.success) {
      const linkData = response?.data.data?.saledata;
      const result = {
        data: response.data,
        amount: "",
        number: "",
        phoneLength: 10,
        name: "",
        image: linkData?.sale_settings?.base_image,
        buttone: linkData?.sale_settings?.pay_detail_button,
        nameOption: linkData?.sale_settings?.name_value_required,
        paymentData: linkData?.payment_data,
        payLink: linkData?.paylink_id,
        subActive: response?.data.data.subscription_active,
        saleId: linkData?.id,
        subscribeData: response?.data?.data?.subscribe,
        memberPay: linkData?.sale_settings?.user_subscribe,
        defaultOptions: [],
        urlOptions: [],
        payAccountDetails: null,
        selection: "",
        pledgeAmount: "",
        isApiAmount: false,
      };

      if (
        linkData?.sale_type !== "Collection" &&
        linkData?.sale_type !== "Wallet"
      ) {
        const contactDetail = linkData?.contact_detail || "";
        result.amount = linkData?.sale_balance || "";
        result.isApiAmount = !!linkData?.sale_balance;
        result.number = contactDetail?.replace("+", "");
        result.phoneLength = contactDetail?.replace("+", "").length || 10;
        result.name = linkData?.contact_name || "";
      } else {
        result.amount = urlAmount || "";
        result.selection = urlOption || "";
        result.number = "";
        result.phoneLength = 10;
        result.name = "";

        result.payAccountDetails = {
          pay_account_values: linkData?.sale_settings?.pay_account_values,
          pay_detail_value: linkData?.sale_settings?.pay_detail_value,
          pay_account_button_visible:
            linkData?.sale_settings?.pay_account_button_visible,
        };

        const payDetailValues = (
          linkData?.sale_settings?.pay_detail_value || ""
        )
          .split(",")
          .map((option) => option.trim())
          .filter(Boolean);

        const payDetailsTargets = JSON.parse(
          linkData?.sale_settings?.pay_details_targets || "{}"
        );

        const isInternalTargets = payDetailsTargets?.isInternalTargets === true;
        const targetArray = isInternalTargets ? [] : (payDetailsTargets?.targets || []);

        const targetsLookup = targetArray.reduce(
          (acc, target) => {
            acc[target.name] = target.amount;
            return acc;
          },
          {}
        );
        // Subcategories apply even when targets are internal (amounts ignored)
        const subCategoriesLookup = (payDetailsTargets?.targets || []).reduce((acc, target) => {
          acc[target.name] = target.subCategories || "";
          return acc;
        }, {});

        result.urlOptions = payDetailValues.map((payDetail) => ({
          value: payDetail,
          target: targetsLookup[payDetail] || 0,
          subCategories: subCategoriesLookup[payDetail] || "",
        }));
      }

      if (linkData?.sale_settings?.amounts_default) {
        result.defaultOptions = linkData?.sale_settings?.amounts_default
          .split(",")
          ?.map((amount) => amount.trim());
      }

      return result;
    } else {
      throw new Error(response?.data?.message || "Failed to fetch data");
    }
  } catch (err) {
    throw err;
  }
};

export const handleGetPdfDetails = async (org_id) => {
  try {
    const response = await axios.get(`${getDocSettingsUrl}/${org_id}`);
    return {
      docImageUrl: response?.data?.data?.doc_image_url,
      docTemplate: response?.data?.data?.doc_template || "default",
      docHexCode: response?.data?.data?.doc_hex_code || "#2FB5B0",
      docTermsService:
        response?.data?.data?.doc_terms_service || "Terms and conditions apply",
    };
  } catch (err) {
    throw err;
  }
};

export const getMyImages = async (org_id) => {
  try {
    const response = await axios.get(`${imagesUrl}/logo/${org_id}`);
    return `'${response?.data?.data?.bs64data}'`;
  } catch (error) {
    throw error;
  }
};

export const checkName = async (org_id, number, nameOption) => {
  try {
    const response = await axios.get(`${namesUrl}phone=${number}&org=${org_id}`);
    return response.data && Object.keys(response.data.data).length !== 0
      ? response?.data?.data?.contact_name
      : !nameOption
        ? "anonymous"
        : "";
  } catch (err) {
    throw err;
  }
};

export const checkNameSurvey = async (org_id, number) => {
  try {
    const response = await axios.get(`${namesUrl}phone=${number}&org=${org_id}`);
    if (response.data && Object.keys(response.data.data).length !== 0) {
      return {
        name: response?.data?.data?.contact_name || "",
        contactId: response?.data?.data?.id || "",
      };
    }
    return { name: "", contactId: "" };
  } catch (err) {
    throw err;
  }
};

export const checkRegisteredMember = async (sale_to, number) => {
  try {
    const response = await axios.get(
      `${groupMemberSearchUrl}/${sale_to}?phone=${number}`
    );
    // The search returns an array of matches; tolerate a single object too
    const payload = response?.data?.data;
    const match = Array.isArray(payload) ? payload[0] : payload;
    const found = !!match && Object.keys(match).length !== 0;
    return {
      found,
      name: found ? match?.contact_name || "" : "",
      contactId: found ? match?.id || "" : "",
    };
  } catch (err) {
    return { found: false, name: "", contactId: "" };
  }
};

export const checkPledgeName = async (org_id, pledgeNumber) => {
  try {
    const response = await axios.get(
      `${namesUrl}phone=${pledgeNumber}&org=${org_id}`
    );
    return response.data && Object.keys(response.data.data).length !== 0
      ? response?.data?.data?.contact_name
      : "";
  } catch (err) {
    throw err;
  }
};

export const handlePay = async ({
  org_id,
  sales_code,
  amount,
  number,
  paymentData,
  name,
  recurring,
  selection,
  type,
  payType,
  saleId,
  other_details,
  selection_details,
  pay_account_details,
}) => {
  let paylink_id;
  let pay_type;
  let pay_account;
  let multi_names = false;

  if (pay_account_details?.pay_account_button_visible) {
    switch (
    pay_account_details?.pay_account_values?.trim()?.toLocaleLowerCase()
    ) {
      case "name_value":
        pay_account = name;
        break;
      case "pay_detail_value":
        pay_account =
          selection_details?.length === 1
            ? selection_details[0].name?.trim()
            : selection_details?.length > 1
              ? selection_details
                .map((detail) => detail.name)
                .join(", ")
                ?.trim()
              : selection.trim() || null;
        break;
      default:
        break;
    }
  } else {
    pay_account = null;
  }

  if (payType === "card") {
    const cardPayment = paymentData?.find(
      (payment) => payment?.link_type === "CARD"
    );
    if (cardPayment) {
      paylink_id = cardPayment?.link_id;
      pay_type = cardPayment?.pay_type;
    }
  }

  if (
    other_details?.length > 0 ||
    (selection_details && selection_details?.length > 1)
  ) {
    let transaction_time = new Date().getTime();
    multi_names = true;

    // Determine if selection_details is present
    const isSelectionDetailsPresent = selection_details?.length > 0;

    // Calculate total amount from the selected details
    let totalAmount;
    let payDetails;
    let updatedOtherDetails;

    if (isSelectionDetailsPresent) {
      totalAmount = selection_details.reduce(
        (sum, detail) => sum + parseFloat(detail.amount || 0),
        0
      );
      payDetails = selection_details.map((detail) => ({
        name: detail.name,
        amount: parseFloat(detail.amount) || 0,
      }));
      updatedOtherDetails = [{ name: name, amount: amount }];
    } else {
      totalAmount = other_details.reduce(
        (sum, detail) => sum + parseFloat(detail.amount || 0),
        0
      );
      let remainingAmount = amount - totalAmount;
      let adjustedAmount = Math.max(0, remainingAmount);
      payDetails = [{ name: selection, amount: amount }];
      updatedOtherDetails = [
        ...other_details,
        { name: name, amount: adjustedAmount },
      ];
    }

    let data = {
      sale_id: saleId,
      amount: amount,
      phone_number: number.replace("+", ""),
      customer_name: name,
      transaction_time,
      pay_details: payDetails,
      other_details: updatedOtherDetails,
    };

    // console.log(data);
    handleMultinames(data);
  }

  if (!paylink_id) {
    const defaultPayment = paymentData?.find(
      (payment) => payment?.link_type !== "CARD"
    );
    if (defaultPayment) {
      paylink_id = defaultPayment?.link_id;
      pay_type = defaultPayment?.pay_type;
    }
  }

  if (paylink_id) {
    let data = {
      org_id: org_id,
      sale_trx_code: sales_code,
      pay_amount: amount.toString(),
      pay_number: number.replace("+", ""),
      paylink_id: paylink_id,
      pay_name: name || "Anonymous",
      pay_details:
        selection_details?.length === 1
          ? selection_details[0].name
          : selection_details?.length > 1
            ? selection_details.map((detail) => detail.name).join(", ")
            : selection || sales_code,
      link_source: "web",
      subscribe_recuring: recurring,
      pay_type: pay_type,
      multi_names,
    };

    if (pay_account) {
      data.pay_account = pay_account;
    }

    // Now, stringify the object
    data = JSON.stringify(data);
    try {
      const response = await axios.post(
        payType === "card" ? payCardUrl : payUrl,
        data,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      if (response?.data?.status === 201) {
        let checkoutUrl = null;
        if (payType === "card") {
          if (response?.data?.data?.data?.authorization_url) {
            checkoutUrl = response?.data?.data?.data?.authorization_url;
          } else {
            return { success: false, message: "an error occurred! please try again" };
          }
        }
        return {
          success: true,
          message: response?.data?.message,
          checkoutUrl,
          requestId: response?.data?.data?.[0]?.id,
        };
      } else {
        return { success: false, message: "an error occurred! please check your details" };
      }
    } catch (err) {
      return { success: false, message: "an error occurred!" };
    }
  } else {
    return { success: false, message: "Payment method not yet activated!" };
  }
};

export const handlePaySub = async ({
  sales_code,
  subAmount,
  number,
  subscribeData,
  org_id,
}) => {
  if (subscribeData?.code && subscribeData?.link_id && subscribeData?.org) {
    let data = JSON.stringify({
      org_id: subscribeData?.org,
      sale_trx_code: subscribeData?.code,
      pay_amount: subAmount.toString(),
      pay_number: number.replace("+", ""),
      paylink_id: subscribeData?.link_id,
      pay_name: "Anonymous",
      pay_details: sales_code,
      link_source: "web",
      subscribe_recuring: false,
      pay_type: "MPESA",
      multi_names: false,
    });

    try {
      const response = await axios.post(payUrl, data, {
        headers: { "Content-Type": "application/json" },
      });

      if (response?.data?.status === 201) {
        return { success: true, message: response?.data?.message };
      } else {
        return { success: false, message: "an error occurred! please try again!" };
      }
    } catch (err) {
      return { success: false, message: "an error occurred!" };
    }
  }
  return { success: false, message: "Invalid subscription data" };
};

const handleMultinames = (data) => {
  try {
    axios.post(multinamesUrl, data);
  } catch (err) { }
};

// Looks up one payment by the request id returned when it was triggered, so an
// older transaction from the same phone number can never be picked up.
// Returns { status: "paid" | "failed" | "pending", data? }.
export const checkTransactionByRequestId = async (requestId, amount) => {
  try {
    const response = await axios.post(checkTransactionUrl, {
      request_ids: [requestId],
    });
    const trx = response?.data?.data?.[0];
    if (!trx) return { status: "pending" };
    // M-Pesa result code 0 = success, anything else = failed/cancelled
    if (String(trx.result_code) !== "0") return { status: "failed" };
    return {
      status: "paid",
      data: {
        paidBy: trx.pay_name || "",
        phoneNumberBy: trx.phone_number || "",
        transactionCode: trx.mpesa_receipt_number || "",
        amount: trx.amount || amount,
      },
    };
  } catch (error) {
    return { status: "pending" };
  }
};

// Polls a payment by request id until it is paid, failed or times out.
// Resolves to { status: "paid" | "failed" | "timeout", data? }.
export const pollTransactionByRequestId = async (
  requestId,
  amount,
  { initialDelayMs = 5000, intervalMs = 4000, timeoutMs = 120000 } = {}
) => {
  const sleep = (delay) => new Promise((resolve) => setTimeout(resolve, delay));
  await sleep(initialDelayMs);
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    const res = await checkTransactionByRequestId(requestId, amount);
    if (res.status === "paid" || res.status === "failed") return res;
    await sleep(intervalMs);
  }
  return { status: "timeout" };
};

export const handleRecheck = async (org_id, number, amount) => {
  const params = {
    phone: number?.replace(/^0/, "254"),
    org: org_id,
  };
  try {
    const response = await axios.get(confirmPaymentUrl, { params: params });
    if (response?.data?.data?.paid_by) {
      return {
        success: true,
        data: {
          paidBy: response?.data?.data?.paid_by || "",
          phoneNumberBy: response?.data?.data?.phone_number || "",
          transactionCode: response?.data?.data?.trxn_code || "",
          amount: response?.data?.data?.amount_paid || amount,
        },
      };
    } else {
      return { success: false };
    }
  } catch (error) {
    return { success: false };
  }
};

export const handlePledge = async ({
  org_id,
  sales_code,
  pledgeNumber,
  customerName,
  pledgeAmount,
  selection,
}) => {
  let data = JSON.stringify({
    org_id: org_id,
    sale_trx_code: sales_code,
    phone_number: pledgeNumber,
    customer_name: customerName,
    amount: pledgeAmount,
    payment_source: "CREDIT",
    pay_details: selection,
  });

  try {
    const response = await axios.post(pledgeUrl, data, {
      headers: { "Content-Type": "application/json" },
    });

    if (response?.data?.success) {
      return { success: true, message: "Thank you for your pledge!" };
    } else {
      return { success: false, message: "an error occurred! please check your details" };
    }
  } catch (err) {
    return { success: false, message: "an error occurred!" };
  }
};

export const handleGeneratePdf = async ({
  docTemplate,
  dataSale,
  docTermsService,
  docHexCode,
  docImageUrl,
  data,
  paymentData,
  original_date,
}) => {
  let dataLoad = {
    doc_type: docTemplate,
    sale_trx_code: dataSale.sale_trx_code,
    sale_amount: dataSale.sale_amount,
    sale_balance: dataSale.sale_balance,
    sale_type: dataSale.sale_type?.toLowerCase(),
    tax: dataSale.sale_tax || 0.0,
    discount: dataSale.sale_discount || 0.0,
    client_number: dataSale.contact_detail,
    client_name: dataSale.contact_name,
    tag_line: docTermsService,
    business_name: data?.data.saledata.org_data.name || "My Business",
    business_location: dataSale.org_data.location,
    business_email: null,
    business_number: dataSale.org_data.main_phone_number,
    hex_color: docHexCode,
    business_category: dataSale.org_data.description,
    business_logo: docImageUrl,
    sale_status: dataSale.sale_status,
    sales_date: original_date,
    due_date: original_date,
    nobuk_logo: "https://www.nobuk.africa/assets/img/nobuklogo1.png",
    payment_details: [
      paymentData?.pay_detail_2
        ? {
          type: paymentData?.pay_type_main,
          number: paymentData?.pay_detail_1,
          account: paymentData.pay_detail_2,
        }
        : {
          type: paymentData?.pay_type_main,
          number: paymentData?.pay_detail_1,
        },
    ],
    items_data: dataSale.items_data,
  };

  try {
    const response = await axios.post(docUrl, dataLoad);
    const base64String = response?.data?.data?.base64pdf;
    const fileName = `${dataSale.sale_trx_code?.toLowerCase()}`;
    if (base64String) {
      return { success: true, base64: base64String, fileName };
    } else {
      return { success: false, message: "Download Failed!" };
    }
  } catch (err) {
    return { success: false, message: "Download Failed!" };
  }
};

export const isCreditPayment = (payment) =>
  String(payment?.item_paid_by || "").trim().toUpperCase() === "CREDIT";

export const handleGenerateStatements = async ({
  details,
  number,
  paid_by,
  from = false,
}) => {
  const capitalize = (str) => str?.charAt(0).toUpperCase() + str?.slice(1);
  const formatPaymentsData = (payments) =>
    payments.map((payment) => ({
      contact: payment.customer_phone,
      category: payment.account,
      amount: payment.payment_amount,
      payment_mode: payment.item_paid_by,
      date: formatPayDate(payment.payment_time),
    }));

  const prepareCombinedData = (
    details,
    paymentsData,
    contributorName,
    contributorNumber,
    totalPaid
  ) => ({
    collection_name: details?.contact_name || details?.collection,
    collection_owner: details?.org_data?.name || details?.name,
    collection_code: details?.sale_trx_code || details?.collection_code,
    end_date: timeDate(details?.sales_date || details?.end_date),
    contributor_name: contributorName,
    contributor_number: contributorNumber,
    total_paid: totalPaid,
    collection_type: capitalize(
      details?.sale_settings?.template_type || details?.template
    ),
    tag_line: details?.tag_line || "Powered by Nobuk",
    payments_data: formatPaymentsData(paymentsData),
  });

  try {
    let paymentsData;

    // Determine data source based on the `from` flag
    if (!from) {
      const response = await axios.post(contributorsStatementsUrl, {
        sale_id: details?.id,
        customer_name: paid_by,
      });

      if (response?.data?.success) {
        paymentsData = response.data.data || [];
      } else {
        return { success: false, message: "Data not ready, please try again later." };
      }
    } else {
      paymentsData = details?.transactions || [];
    }

    if (!Array.isArray(paymentsData) || paymentsData.length === 0) {
      return { success: false, message: "No data found" };
    }

    // CREDIT rows are the amount still owed, not money paid
    const totalPaid = paymentsData
      .filter((payment) => !isCreditPayment(payment))
      .reduce((acc, payment) => acc + payment.payment_amount, 0);

    const combinedData = prepareCombinedData(
      details,
      paymentsData,
      paid_by,
      number,
      totalPaid
    );

    // Post data to generate the PDF
    const pdfResponse = await axios.post(statementsUrl, combinedData);

    if (pdfResponse?.data?.data?.base64pdf) {
      return {
        success: true,
        base64: pdfResponse.data.data.base64pdf,
        fileName: `${details?.sale_trx_code?.toLowerCase() || "statement"}`,
      };
    } else {
      return { success: false, message: "Not yet ready, please try again in 15 seconds" };
    }
  } catch (err) {
    return { success: false, message: "Download Failed!" };
  }
};

export const getSurveyQuestions = async (org_id) => {
  try {
    const response = await axios.get(`${surveysUrl}/${org_id}`);
    return { success: true, data: response?.data };
  } catch (error) {
    return { success: false, data: null };
  }
};

export const callSurveyAPI = async (id) => {
  try {
    const response = await axios.get(`${surveysUrl}/${id}`);
    return { success: true, data: response?.data };
  } catch (error) {
    throw new Error(`API call failed: ${error}`);
  }
};

export const getGroupContacts = async (groupId) => {
  try {
    const response = await axios.get(`${groupContactsUrl}${groupId}`);
    return { success: true, data: response?.data };
  } catch (error) {
    return { success: false, data: null };
  }
};

export const handlePaySurvey = async ({
  org_id,
  sales_code,
  amount,
  number,
  paymentData,
  name,
  payType,
  selection,
  selection_details,
  saleId,
}) => {
  let paylink_id;
  let pay_type;
  let pay_account;

  if (payType === "card") {
    const cardPayment = paymentData?.find(
      (payment) => payment?.link_type === "CARD"
    );
    if (cardPayment) {
      paylink_id = cardPayment?.link_id;
      pay_type = cardPayment?.pay_type;
    }
  }

  if (!paylink_id) {
    const defaultPayment = paymentData?.find(
      (payment) => payment?.link_type !== "CARD"
    );
    if (defaultPayment) {
      paylink_id = defaultPayment?.link_id;
      pay_type = defaultPayment?.pay_type;
    }
  }

  if (paylink_id) {
    if (selection_details && selection_details.length > 0) {
      let transaction_time = new Date().getTime();

      const payDetailsList = selection_details.map((detail) => ({
        name: detail.name,
        amount: parseFloat(detail.amount) || 0,
      }));

      const multiData = {
        sale_id: saleId,
        amount: amount,
        phone_number: number.replace("+", ""),
        customer_name: name,
        transaction_time,
        pay_details: payDetailsList,
        other_details: [{ name: name, amount: amount }],
      };

      handleMultinames(multiData);
    }

    let data = {
      org_id: org_id,
      sale_trx_code: sales_code,
      pay_amount: amount.toString(),
      pay_number: number.replace("+", ""),
      paylink_id: paylink_id,
      pay_name: name || "Anonymous",
      pay_details: selection_details?.length > 0
        ? selection_details.map((detail) => detail.name).join(", ")
        : selection || sales_code,
      link_source: "web",
      subscribe_recuring: false,
      pay_type: pay_type,
      multi_names: selection_details?.length > 0 ? true : false,
    };

    if (pay_account) {
      data.pay_account = pay_account;
    }

    // Now, stringify the object
    data = JSON.stringify(data);
    try {
      const response = await axios.post(
        payType === "card" ? payCardUrl : payUrl,
        data,
        {
          headers: { "Content-Type": "application/json" },
        }
      );
      if (response?.data?.status === 201) {
        if (payType === "card") {
          const authUrl = response?.data?.data?.data?.authorization_url;
          const reqId = response?.data?.data?.data?.request_id;
          if (authUrl) {
            return { authorization_url: authUrl, request_id: reqId };
          }
        }
        const requestId = response?.data?.data?.[0]?.id;
        return { success: true, requestId, message: response?.data?.message };
      } else {
        return { success: false, message: "an error occurred! please try again" };
      }
    } catch (err) {
      return { success: false, message: "an error occurred!" };
    }
  } else {
    return { success: false, message: "Payment method not yet activated!" };
  }
};

export const handleSurveySubmit = async ({
  group_id,
  survey_id,
  contact_exists,
  contact_id,
  contact_name,
  response_data,
}) => {
  let data = {
    group_id: group_id,
    contact_exists: contact_exists,
    survey_id: survey_id,
    contact_id: contact_id,
    contact_name: contact_name,
    response_data: response_data,
  };

  try {
    const response = await axios.post(surveysSubmitUrl, data);

    if (response?.data?.success) {
      return { success: true, message: "Thank you for your time!" };
    } else {
      return { success: false, message: "an error occurred! please retry" };
    }
  } catch (err) {
    return { success: false, message: "an error occurred!" };
  }
};

export const handleGetReceiptDetails = async (mpesaCode) => {
  let dataLoad = {
    code: mpesaCode?.toUpperCase(),
  };

  try {
    const response = await axios.post(getReceiptDetailsUrl, dataLoad);
    if (response?.data?.success) {
      return { success: true, data: response?.data?.data, message: "Payment found!" };
    } else {
      return { success: false, message: "Payment record not found!" };
    }
  } catch (err) {
    return { success: false, message: "Payment record not found!" };
  }
};

export const handleGetStatementDetails = async (mpesaCode) => {
  try {
    const response = await axios.get(
      `${contributorsStatementsUrl}?trx=${mpesaCode}`
    );
    if (response?.data?.success) {
      return { success: true, data: response?.data?.data, message: "Payment records found!" };
    } else {
      return { success: false, message: "Payment records not found!" };
    }
  } catch (err) {
    return { success: false, message: "Payment records not found!" };
  }
};


